// Copyright (c) 2021 WiseTime. All rights reserved.

import { DateTime } from "luxon"
import { DaySummary } from "../../../../generated/wisetime/user/timeline/v4/timeline_pb"
import { TimelineDate, toDate } from "../../dateTime"
import {
  DayExpectation,
  MemberExpectation
} from "../../../../generated/legebill/site/postexpectation/v4/post_expectation_pb"

export const workHoursPtsMultiplier = 100

export type TileType = "no-record" | "posted" | "unposted"

export enum TilesViewType {
  GRID = 1,
  GRAPH = 2,
}

export const getType = (daySummary: Partial<DaySummary.AsObject> | undefined): TileType => {
  const totalPosted = (daySummary?.timePostedToSiteList ?? [])
    .map(daySummary => daySummary.totalPosted)
    .reduce((i, duration) => i + (duration?.seconds ?? 0), 0) ?? 0
  const totalUnposted = daySummary?.totalUnposted?.seconds ?? 0
  if (totalPosted > 0 && totalUnposted === 0) {
    return "posted"
  }
  if (totalUnposted > 0) {
    return "unposted"
  }
  return "no-record"
}

export const getOpacity = (seconds: number): number => {
  const hours = seconds / 60 / 60
  if (hours < 2) {
    return 0.4
  }
  if (hours < 4) {
    return 0.6
  }
  if (hours < 6) {
    return 0.8
  }
  return 1
}

export const getTilePostedColor = (opacity = 1): string => `rgba(205, 230, 210, ${opacity})`
export const getTileUnpostedColor = (opacity = 1): string => `rgba(217, 222, 241, ${opacity})`

export const defaultHours = {
  min: 8,
  max: 24,
  space: 2,
}

/* eslint-disable lingui/no-unlocalized-strings */
export const getGraphBackground = (
  postedSeconds: number,
  unpostedSeconds: number,
  hoursCount = defaultHours.min,
  tileSize = 40,
): string => {
  const totalPercentage = 100
  const secondsInHour = 3600
  const minHeight = 3
  const hoursInSeconds = hoursCount * secondsInHour
  const minPercentage = minHeight / (tileSize / totalPercentage)
  const postedColor = "#bbdcc4"
  const unpostedColor = "#e4e5f2"
  const gradientStart = "linear-gradient(360deg, "

  const convertToPercentage = (seconds: number): number => {
    const percentage = seconds / (hoursInSeconds / totalPercentage)
    if (percentage === 0) {
      return percentage
    }
    if (percentage < minPercentage) {
      return minPercentage
    }
    return Number(percentage.toFixed(2))
  }

  const exactPostedPercentage = convertToPercentage(postedSeconds)
  const exactUnpostedPercentage = convertToPercentage(unpostedSeconds)

  const handleMinPercentage = (firstPercentage: number, secondPercentage: number): number =>
    secondPercentage === minPercentage && firstPercentage > minPercentage * 2
      ? firstPercentage - minPercentage : firstPercentage

  const postedPercentage = handleMinPercentage(exactPostedPercentage, exactUnpostedPercentage)
  const unpostedPercentage = handleMinPercentage(exactUnpostedPercentage, exactPostedPercentage)

  const isOverLimit = unpostedPercentage + postedPercentage > totalPercentage

  if (isOverLimit) {
    if (postedPercentage === 0) {
      return unpostedColor
    }
    if (unpostedPercentage === 0) {
      return postedColor
    }
    const maxHeight = totalPercentage - minPercentage
    const postedEnd = postedPercentage > maxHeight ? maxHeight : postedPercentage
    return gradientStart +
      `${postedColor} 0 ${postedEnd}%, ` +
      `${unpostedColor} ${postedEnd}% ${totalPercentage}%)`
  }
  if (postedPercentage > 0 && unpostedPercentage === 0) {
    return gradientStart +
      `${postedColor} 0 ${postedPercentage}%, ` +
      `transparent ${postedPercentage}% ${totalPercentage}%)`
  }
  if (unpostedPercentage > 0 && postedPercentage === 0) {
    return gradientStart +
      `${unpostedColor} 0 ${unpostedPercentage}%, ` +
      `transparent ${unpostedPercentage}% ${totalPercentage}%)`
  }
  return gradientStart +
    `${postedColor} 0 ${postedPercentage}%, ` +
    `${unpostedColor} ${postedPercentage}% ${postedPercentage + unpostedPercentage}%, ` +
    `transparent ${postedPercentage + unpostedPercentage}% ${totalPercentage}%)`
}
/* eslint-enable lingui/no-unlocalized-strings */

export const getExpectationLinePosition = (workHoursPts: number, hoursCount = defaultHours.min): string => {
  const expectedHours = workHoursPts / workHoursPtsMultiplier
  const getPercentage = () => {
    if (expectedHours >= hoursCount) {
      return 100
    }
    return parseFloat((expectedHours / (hoursCount / 100)).toFixed(2))
  }
  return `calc(${getPercentage()}% - 1px)`
}

export const workHoursPtsToSeconds = (workHoursPts: number): number => workHoursPts / workHoursPtsMultiplier * 60 * 60

export const getDayExpectation = (
  timelineDate: TimelineDate,
  memberExpectations: MemberExpectation.AsObject[],
): DayExpectation.AsObject | undefined => {
  const currentDate = toDate(timelineDate)
  const matchedDayExpectations = memberExpectations.reduce((
    result: DayExpectation.AsObject[],
    memberExpectation: MemberExpectation.AsObject,
  ): DayExpectation.AsObject[] => {
    const dayExpectationsList = memberExpectation.dayExpectationsList
      .filter((wd: DayExpectation.AsObject) => (
        wd.date?.year === currentDate.year
        && wd.date?.month === currentDate.month
        && wd.date?.day === currentDate.day
      ))

    if (dayExpectationsList.length !== 0) {
      return result.concat(dayExpectationsList)
    }

    return result
  }, [])

  return matchedDayExpectations
    .reduce((
      prev: DayExpectation.AsObject | undefined,
      current: DayExpectation.AsObject,
    ): DayExpectation.AsObject | undefined => (prev?.workHoursPts ?? 0) > current.workHoursPts ? prev : current, undefined)
}

export const getTileHoursCount = (
  memberExpectations: MemberExpectation.AsObject[],
  start: TimelineDate,
  end: TimelineDate,
): number => {
  const startDateMillis = toDate(start).toMillis()
  const endDateMillis = toDate(end).toMillis()
  const matchedDayExpectations = memberExpectations.reduce((
    result: DayExpectation.AsObject[],
    memberExpectation: MemberExpectation.AsObject,
  ): DayExpectation.AsObject[] => {
    const dayExpectationsList = memberExpectation.dayExpectationsList
      .filter((dayExpectation: DayExpectation.AsObject) => {
        if (dayExpectation.date === undefined) {
          return false
        }
        const expectationDateMillis = DateTime.fromObject(dayExpectation.date).toMillis()
        return expectationDateMillis >= startDateMillis && expectationDateMillis <= endDateMillis
      })

    if (dayExpectationsList.length !== 0) {
      return result.concat(dayExpectationsList)
    }

    return result
  }, [])
  const maxExpectedHoursCount = matchedDayExpectations
    .reduce((
      prev: DayExpectation.AsObject | undefined,
      current: DayExpectation.AsObject,
    ): DayExpectation.AsObject | undefined => ((prev?.workHoursPts ?? 0) > current.workHoursPts) ? prev : current, undefined)
    ?.workHoursPts ?? 0
  const expectedHoursCountWithSpace = Math.ceil((maxExpectedHoursCount / workHoursPtsMultiplier)) + defaultHours.space

  if (expectedHoursCountWithSpace >= defaultHours.min && expectedHoursCountWithSpace <= defaultHours.max) {
    return expectedHoursCountWithSpace
  }

  if (expectedHoursCountWithSpace > defaultHours.max) {
    return defaultHours.max
  }

  return defaultHours.min
}
